import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { add, sub } from "date-fns";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import AugmentationCard from "./components/AugmentationCard";
import BloodPressureMetricsCard from "./components/BloodPressureMetricsCard";
import CardMeasure from "./components/MeasureCard";
import { FEATURE_FLAGS } from "utils/constants";
import { get, getFeatureFlag, isAdmin } from "utils/io";

const getMeasures = async (hubId, takenAfter = undefined) => {
  try {
    const measureRes = await get("/measures", {
      esn: hubId,
      taken_after:
        takenAfter?.toISOString() ||
        sub(new Date(), { weeks: 2 }).toISOString(),
      limit: 5000,
    });
    const newMeasures = measureRes.data.measures;
    return newMeasures;
  } catch (err) {
    enqueueSnackbar("Failed to get recent measurements", {
      variant: "error",
    });
    return [];
  }
};

export default function Hub({ hubId }) {
  const [measures, setMeasures] = useState([]);
  const [lastMeasureTime, setLastMeasureTime] = useState();
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const getLatestMeasures = useCallback(async () => {
    setRefreshLoading(true);
    const additionalMeaures = await getMeasures(
      hubId,
      lastMeasureTime
        ? add(new Date(lastMeasureTime), { seconds: 1 })
        : undefined
    );
    setMeasures([...additionalMeaures, ...measures]);
    if (additionalMeaures.length > 0) {
      setLastMeasureTime(additionalMeaures[0].timestamp);
    }
    setRefreshLoading(false);
  }, [lastMeasureTime, measures, hubId]);

  const getMeasureDetails = [
    {
      title: "Brachial Blood Pressure",
      value: `${measures[0]?.value?.systolic}/${measures[0]?.value?.diastolic}`,
      unit: "mmHg (Sys/Dia)",
    },
    {
      title: "Pulse",
      value: measures[0]?.value?.bpm,
      unit: "BPM",
    },
    {
      title: "Central Pulse Pressure",
      value: measures[0]?.value?.central_pulse_pressure,
      unit: "mmHg",
      valueColor: "#92C8A0",
    },
    {
      title: "Pulse Pressure Amplification",
      value: measures[0]?.value?.pulse_pressure_amplification,
      unit: "%",
      valueColor: "#8784D2",
    },
    {
      title: "Central Systolic Pressure",
      value: measures[0]?.value?.central_systolic,
      unit: "mmHg",
      valueColor: "#175FC8",
    },
    {
      title: "Augmentation Index",
      value: measures[0]?.value?.augmentation_index,
      unit: "%",
    },
    {
      title: "Augmentation Pressure",
      value: measures[0]?.value?.augmentation_pressure,
      unit: "mmHg",
    },
    {
      title: "SEVR",
      value: measures[0]?.value?.subendocardial_viability_ratio,
      unit: "%",
    },
  ];

  useEffect(() => {
    const getMeasuresWithLoading = async () => {
      setInitialLoading(true);
      const newMeasures = await getMeasures(hubId);
      setMeasures(newMeasures);
      setLastMeasureTime(newMeasures[0]?.timestamp);
      setAutoRefresh(true);
      setInitialLoading(false);
    };

    getMeasuresWithLoading();
  }, [hubId]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(getLatestMeasures, 10000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [autoRefresh, getLatestMeasures]);

  return initialLoading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  ) : (
    <>
      <Stack
        sx={{ my: 2, alignItems: "center", justifyContent: "space-between" }}
        direction="row"
      >
        <Typography fontWeight="bold" fontSize="20px">
          Hub {hubId}
        </Typography>
        <Stack direction="row" spacing={1}>
          {(isAdmin() || !getFeatureFlag(FEATURE_FLAGS.REALTIME_PROTOCOL)) && (
            <Tooltip
              title={
                <Typography variant="body2">
                  Refresh measurements every 10 seconds
                </Typography>
              }
              arrow
            >
              <FormControlLabel
                control={<Switch checked={autoRefresh} />}
                onChange={() => setAutoRefresh(!autoRefresh)}
                label="Auto Refresh"
                labelPlacement="start"
              />
            </Tooltip>
          )}
          <LoadingButton
            loading={refreshLoading}
            onClick={getLatestMeasures}
            variant="contained"
          >
            Refresh
          </LoadingButton>
        </Stack>
      </Stack>
      <Stack
        sx={{ border: "1px solid #0000001F", p: "16px", borderRadius: "12px" }}
        spacing={4}
      >
        <Grid container spacing={2}>
          {getMeasureDetails.map((item) => (
            <Grid key={item.title} size={{ xs: 12, sm: 6, md: 3 }}>
              <CardMeasure {...item} time={measures[0].timestamp} />
            </Grid>
          ))}
        </Grid>
        <BloodPressureMetricsCard measures={measures} />
        <AugmentationCard measures={measures} />
      </Stack>
    </>
  );
}

Hub.propTypes = {
  hubId: PropTypes.string.isRequired,
};
